import { ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@utils/cn'
import { useIsDesktop } from '@hooks/useMediaQuery'

/**
 * Secção colapsável apenas em ecrãs verticais/mobile (< lg).
 * Em desktop mantém sempre o conteúdo visível.
 * Ícone: seta down (fechado) que roda 180° em 1s para up (aberto).
 */
interface CollapsibleSectionProps {
  readonly title: string
  readonly subtitle?: string
  readonly isOpen: boolean
  readonly onToggle: () => void
  readonly children: ReactNode
  readonly className?: string
  readonly headerClassName?: string
  /** Conteúdo extra no header (ex: badge, botão Editar) */
  readonly headerExtra?: ReactNode
  /** Em desktop (lg+): não colapsar, sempre mostrar */
  readonly collapsibleOnlyOnMobile?: boolean
}

export function CollapsibleSection({
  title,
  subtitle,
  isOpen,
  onToggle,
  children,
  className,
  headerClassName,
  headerExtra,
  collapsibleOnlyOnMobile = true,
}: Readonly<CollapsibleSectionProps>) {
  const isDesktop = useIsDesktop()
  const isCollapsible = collapsibleOnlyOnMobile ? !isDesktop : false
  const handleToggle = () => isCollapsible && onToggle()
  const hasHeaderExtra = headerExtra != null && headerExtra !== false

  return (
    <div className={cn('rounded-3xl border border-secondary-200 bg-white shadow-sm', className)}>
      {/* Header: div evita botão dentro de botão quando headerExtra tem botões (ex: Adicionar Peça) */}
      <div
        role={isCollapsible ? 'button' : undefined}
        tabIndex={isCollapsible ? 0 : undefined}
        onClick={isCollapsible ? handleToggle : undefined}
        onKeyDown={isCollapsible ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle() } } : undefined}
        aria-expanded={isCollapsible ? isOpen : undefined}
        aria-controls={isCollapsible ? `section-${title.replace(/\s/g, '-')}` : undefined}
        className={cn(
          'w-full p-6 text-left transition-colors',
          hasHeaderExtra ? 'flex flex-col gap-4' : 'flex min-h-[52px] items-center justify-between',
          'lg:cursor-default',
          isCollapsible && 'hover:bg-secondary-50 active:bg-secondary-100 cursor-pointer',
          headerClassName
        )}
      >
        <div
          className={cn(
            'flex w-full min-w-0 items-start justify-between gap-3',
            !hasHeaderExtra && 'flex-1 items-center'
          )}
        >
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-secondary-900">{title}</h2>
            {subtitle && (
              <p className="mt-0.5 break-words text-xs text-secondary-500">{subtitle}</p>
            )}
          </div>
          {!hasHeaderExtra && (
            <div
              className="ml-4 flex flex-shrink-0 items-center gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              {headerExtra}
              {isCollapsible && (
                <span
                  className={cn(
                    'inline-flex transition-transform duration-1000 ease-in-out',
                    isOpen && 'rotate-180'
                  )}
                  aria-hidden
                >
                  <ChevronDown className="h-5 w-5 text-secondary-500" />
                </span>
              )}
            </div>
          )}
          {hasHeaderExtra && isCollapsible && (
            <span
              className={cn(
                'inline-flex flex-shrink-0 transition-transform duration-1000 ease-in-out',
                isOpen && 'rotate-180'
              )}
              aria-hidden
            >
              <ChevronDown className="h-5 w-5 text-secondary-500" />
            </span>
          )}
        </div>
        {hasHeaderExtra && (
          <div
            className="flex w-full flex-wrap items-stretch gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            {headerExtra}
          </div>
        )}
      </div>
      {/* Conteúdo: em mobile só mostra quando aberto; em desktop (lg+) sempre mostra */}
      <div
        id={`section-${title.replace(/\s/g, '-')}`}
        className={cn(
          'border-t border-secondary-100 transition-all duration-300',
          isCollapsible && !isOpen && 'max-h-0 overflow-hidden opacity-0',
          isCollapsible && isOpen && 'max-h-[5000px] opacity-100',
          !isCollapsible && '!max-h-none opacity-100'
        )}
      >
        <div className="px-6 pb-6 pt-4">{children}</div>
      </div>
    </div>
  )
}
